import { CommonModule } from "@angular/common";
import { Component, computed, inject, OnInit, signal, ViewChild } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { Institution, Report, ReportSearchCriteria } from "./report.model";
import { ReportService } from "./report.service";
import { Loader } from '../../shared/components/loader/loader';
import { PageHeader } from "../../shared/components/page-header/page-header";
import { NotificationDialog } from "../../shared/components/notification-dialog/notification-dialog";
import { ConfirmDialog } from "../../shared/components/confirm-dialog/confirm-dialog";
import { DataTable, TableColumn } from "../../shared/components/data-table/data-table";
import { CardPanel } from "../../shared/components/card-panel/card-panel";
import { Button } from "../../shared/components/button/button";
import { InstitutionTransferList } from '../../shared/components/institution-transfer-list/institution-transfer-list';

@Component({
  selector: 'app-report-management',
  imports: [
    CommonModule,
    FormsModule,
    Loader,
    PageHeader,
    NotificationDialog,
    ConfirmDialog,
    DataTable,
    CardPanel,
    Button,
    InstitutionTransferList,
  ],
  templateUrl: './report-management.html',
})
export class ReportManagement implements OnInit {
  @ViewChild(InstitutionTransferList) private transferList?: InstitutionTransferList;

  isCurrentUserAdmin = signal(true);
  protected readonly Math = Math;
  notificationMessage = signal('');
  notificationType = signal<'success' | 'error'>('success');
  isNotificationVisible = signal(false);
  institutions = signal<Institution[]>([]);
  reports = signal<Report[]>([]);
  filteredReports = signal<Report[]>([]);
  currentPage = signal(1);
  pageSize = 10;
  selectedReport = signal<Report | null>(null);
  isEditReport = signal(false);
  isAddReport = signal(false);
  availableInstitutionSearch = signal('');

  searchText = '';
  searchTimeout: any;

  // Form fields
  reportName = signal('');
  pbiUnitId = signal('');
  pbiWorkspace = signal('');
  pbiDataset = signal('');
  isActive = signal(true);
  isInternalReport = signal(false);
  role = signal('');
  pageName = signal('');
  reportType = signal<'RLS' | 'Non-RLS'>('Non-RLS');
  pageView = signal<'actualSize' | 'fitToPage' | 'fitToWidth'>('actualSize');
  filterPaneEnabled = signal(false);
  navContentPaneEnabled = signal(false);
  selectedAvailable = signal<string[]>([]);
  selectedAssigned = signal<string[]>([]);
  assignedInstitutions = signal<Institution[]>([]);
  showDeleteModal = signal(false);
  reportToDelete = signal<Report | null>(null);

  isLoading = signal(false);
  private loadingCount = 0;

  private startLoading(): void { this.loadingCount++; this.isLoading.set(true); }
  private stopLoading(): void  { this.loadingCount = Math.max(0, this.loadingCount - 1); if (this.loadingCount === 0) this.isLoading.set(false); }

  tableColumns: TableColumn[] = [
    { key: 'reportName', label: 'Report Name', sortable: true, width: '30%' },
    { key: 'reportType', label: 'Report Type', align: 'center', sortable: true, width: '14%' },
    { key: 'active', label: 'Active', align: 'center', sortable: true, width: '11%' },
    { key: 'internalReport', label: 'Internal', align: 'center', sortable: true, width: '11%' },
  ];

  searchCriteria: ReportSearchCriteria = {
    reportName: '',
    reportId: '',
    institution: '',
    active: '',
    reportType: '',
    internalReport: '',
  };

  private reportService = inject(ReportService);

  resetSearch(): void {
    this.searchCriteria = {
      reportName: '',
      reportId: '',
      institution: '',
      active: '',
      reportType: '',
      internalReport: '',
    };
    this.fetchReports();
  }

  ngOnInit(): void {
    this.fetchInstitutions();
    this.fetchReports();
  }

  showNotification(message: string, type: 'success' | 'error' = 'success'): void {
    this.notificationMessage.set(message);
    this.notificationType.set(type);
    this.isNotificationVisible.set(true);
  }

  fetchInstitutions(): void {
    this.startLoading();
    this.reportService.getInstitutions().subscribe({
      next: (institutions) => {
        this.institutions.set(institutions || []);
        this.reports.set(this.reports().map(report => this.enrichReportInstitutions(report)));
        this.filteredReports.set(this.filteredReports().map(report => this.enrichReportInstitutions(report)));
        this.stopLoading();
      },
      error: (err) => {
        console.error('Error fetching institutions:', err);
        this.showNotification('Failed to load institutions. Please refresh.', 'error');
        this.stopLoading();
      }
    });
  }

  fetchReports(): void {
    this.onSearch();
  }

  allAvailableInstitutions = computed(() => this.institutions().map(i => i.name));

  paginatedReports = computed(() => {
    const startIndex = (this.currentPage() - 1) * this.pageSize;
    return this.filteredReports().slice(startIndex, startIndex + this.pageSize);
  });

  totalPages = computed(() => {
    return Math.ceil(this.filteredReports().length / this.pageSize) || 1;
  });

  availableInstitutions = computed(() => {
    const all = this.allAvailableInstitutions();
    const assigned = this.assignedInstitutions().map((i) => i.name);
    const available = all
      .filter((inst) => !assigned.includes(inst))
      .sort((a, b) => a.localeCompare(b));
    const search = this.availableInstitutionSearch().toLowerCase();
    if (!search) {
      return available;
    }
    return [...available].sort((a, b) => {
      const aMatch = a.toLowerCase().includes(search);
      const bMatch = b.toLowerCase().includes(search);
      if (aMatch && !bMatch) return -1;
      if (!aMatch && bMatch) return 1;
      return a.localeCompare(b);
    });
  });

  isFormValid = computed(() => {
    return (
      this.reportName().trim().length > 0 &&
      this.pbiUnitId().trim().length > 0 &&
      this.pbiWorkspace().trim().length > 0 &&
      this.assignedInstitutions().length > 0
    );
  });

  onPageChange(page: number): void {
    this.currentPage.set(page);
    const reports = this.paginatedReports();
    if (reports.length > 0) {
      this.onSelectReport(reports[0]);
    }
  }

  onSearch(preserveSelectedReportId?: string, onComplete?: () => void): void {
    this.startLoading();
    const institution = this.institutions().find(item => item.name === this.searchCriteria.institution);
    this.reportService.getReports({
      ...this.searchCriteria,
      institution: institution?.id ?? this.searchCriteria.institution,
    }).subscribe({
      next: (reports) => {
        const sorted = reports.map(report => this.enrichReportInstitutions(report)).sort((a, b) =>
          String(a?.reportName ?? '').localeCompare(String(b?.reportName ?? '')),
        );
        this.filteredReports.set(sorted);
        this.reports.set(sorted);

        if (sorted.length === 0) {
          this.currentPage.set(1);
          this.selectedReport.set(null);
          this.stopLoading();
          onComplete?.();
          return;
        }

        const reportToSelect = preserveSelectedReportId
          ? sorted.find((r) => r.id === preserveSelectedReportId) ?? sorted[0]
          : sorted[0];

        const selectedIndex = Math.max(0, sorted.findIndex((r) => r.id === reportToSelect.id));
        this.currentPage.set(Math.floor(selectedIndex / this.pageSize) + 1);
        this.onSelectReport(reportToSelect);
        this.stopLoading();
        onComplete?.();
      },
      error: (err) => {
        console.error('Error searching reports:', err);
        this.showNotification('Search failed. Please try again.', 'error');
        this.stopLoading();
        onComplete?.();
      }
    });
  }

  private enrichReportInstitutions(report: Report): Report {
    const institutionsById = new Map(this.institutions().map(institution => [institution.id, institution]));
    return {
      ...report,
      assignedInstitutions: report.assignedInstitutions.map(institution =>
        institutionsById.get(institution.id) ?? institution,
      ),
    };
  }

  onSelectReport(report: Report): void {
    if (report.id) {
      this.startLoading();
      this.reportService.getReportById(report.id).subscribe({
        next: (reportDetails) => {
          const enrichedReport = this.enrichReportInstitutions(reportDetails);
          this.selectedReport.set(enrichedReport);
          this.isEditReport.set(false);
          this.isAddReport.set(false);
          this.loadReportDetails(enrichedReport);
          this.stopLoading();
        },
        error: () => {
          this.loadReportDetails(report);
          this.stopLoading();
        }
      });
    }
  }

  private loadReportDetails(report: Report): void {
    this.reportName.set(String(report?.reportName ?? ''));
    this.role.set(report.role);
    this.pageName.set(String(report?.pageName ?? ''));
    this.pbiUnitId.set(report.pbiUnitId);
    this.pbiWorkspace.set(report.pbiWorkspace);
    this.pbiDataset.set(report.pbiDataset);
    this.isActive.set(report.active === 'Yes');
    this.isInternalReport.set(report.internalReport === 'Yes');
    this.reportType.set(report.reportType);
    this.pageView.set(report.pageView);
    this.filterPaneEnabled.set(report.filterPaneEnabled);
    this.navContentPaneEnabled.set(report.navContentPaneEnabled);
    this.assignedInstitutions.set([...report.assignedInstitutions]);
    this.selectedAvailable.set([]);
    this.selectedAssigned.set([]);
  }

  onCreateReport(): void {
    this.isAddReport.set(true);
    this.isEditReport.set(true);
    this.selectedReport.set(null);
    this.reportName.set('');
    this.role.set('');
    this.pageName.set('');
    this.pbiUnitId.set('');
    this.pbiWorkspace.set('');
    this.pbiDataset.set('');
    this.isActive.set(true);
    this.isInternalReport.set(false);
    this.reportType.set('Non-RLS');
    this.pageView.set('actualSize');
    this.filterPaneEnabled.set(false);
    this.navContentPaneEnabled.set(false);
    this.assignedInstitutions.set([]);
    this.selectedAvailable.set([]);
    this.selectedAssigned.set([]);
  }

    onEditReport(): void {
      this.isEditReport.set(true);
    }

    onDeleteReport(report: Report): void {
      this.reportToDelete.set(report);
      this.showDeleteModal.set(true);
    }

    cancelDelete(): void {
      this.showDeleteModal.set(false);
      this.reportToDelete.set(null);
    }

    moveToAssigned(): void {
      const toMove = this.selectedAvailable();
      if (toMove.length === 0) return;
      const institutionsToAdd = this.institutions().filter(inst => toMove.includes(inst.name));
      this.assignedInstitutions.update(assigned => [...assigned, ...institutionsToAdd]);
      this.selectedAvailable.set([]);
    }

    moveToAvailable(): void {
      const toMove = this.selectedAssigned();
      if (toMove.length === 0) return;
      this.assignedInstitutions.update((assigned) =>
        assigned.filter((i) => !toMove.includes(i.name)),
      );
      this.selectedAssigned.set([]);
    }
    moveAllToAssigned(): void {
      const available = this.availableInstitutions();

      if (available.length === 0) return;

      const institutionsToAdd = this.institutions().filter((inst) => available.includes(inst.name));

      this.assignedInstitutions.update((assigned) => [
        ...assigned,
        ...institutionsToAdd.filter((inst) => !assigned.some((a) => a.id === inst.id)),
      ]);

      this.selectedAvailable.set([]);
    }
    moveAllToAvailable(): void {
      this.assignedInstitutions.set([]);
      this.selectedAssigned.set([]);
    }

  onAvailableInstitutionsKeydown(event: KeyboardEvent): void {
    // Ctrl + A
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'a') {
      event.preventDefault();
      this.selectedAvailable.set([...this.availableInstitutions()]);
      return;
    }
    // Ignore non-character keys
    if (event.ctrlKey || event.metaKey || event.altKey || event.key.length !== 1) {
      return;
    }
    this.searchText += event.key.toLowerCase();
    clearTimeout(this.searchTimeout);
    this.availableInstitutionSearch.set(this.searchText);
    this.searchTimeout = setTimeout(() => {
      this.searchText = '';
    }, 1500);
    const match = this.availableInstitutions().find((inst) =>
      inst.toLowerCase().includes(this.searchText),
    );
    if (match) {
      this.selectedAvailable.set([match]);
    }
  }
  onAssignedInstitutionsKeydown(event: KeyboardEvent): void {
    // Ctrl + A
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'a') {
      event.preventDefault();

      if (this.isEditReport()) {
        this.selectedAssigned.set(
          this.assignedInstitutions().map(inst => inst.name)
        );
      }

      return;
    }

    // Ignore non-character keys
    if (
      event.ctrlKey ||
      event.metaKey ||
      event.altKey ||
      event.key.length !== 1
    ) {
      return;
    }

    this.searchText += event.key.toLowerCase();

    clearTimeout(this.searchTimeout);

    this.searchTimeout = setTimeout(() => {
      this.searchText = '';
    }, 1500);

    const match = this.assignedInstitutions().find(inst =>
      inst.name.toLowerCase().includes(this.searchText)
    );

    if (match) {
      this.selectedAssigned.set([match.name]);
    }
  }
  toggleAvailableSelection(inst: string): void {
    const current = this.selectedAvailable();
    if (current.includes(inst)) {
      this.selectedAvailable.set(current.filter(i => i !== inst));
    } else {
      this.selectedAvailable.set([...current, inst]);
    }
  }

  toggleAssignedSelection(inst: string): void {
    const current = this.selectedAssigned();
    if (current.includes(inst)) {
      this.selectedAssigned.set(current.filter(i => i !== inst));
    } else {
      this.selectedAssigned.set([...current, inst]);
    }
  }

  onSave(): void {
    const name = this.reportName().trim();
    if (!name) {
      this.showNotification('Report Name is required.', 'error');
      return;
    }
    if (!this.pbiUnitId().trim()) {
      this.showNotification('PBI Unit ID is required.', 'error');
      return;
    }
    if (!this.pbiWorkspace().trim()) {
      this.showNotification('PBI Workspace is required.', 'error');
      return;
    }
    if (this.assignedInstitutions().length === 0) {
      this.showNotification('At least one institution must be assigned.', 'error');
      return;
    }

    const newReport: Report = {
      id: this.selectedReport()?.id,
      reportId: this.selectedReport()?.reportId || '',
      reportName: name,
      role: this.role(),
      pageName: this.pageName().trim(),
      pbiUnitId: this.pbiUnitId().trim(),
      pbiWorkspace: this.pbiWorkspace().trim(),
      pbiDataset: this.reportType() === 'RLS' ? this.pbiDataset().trim() : '',
      active: this.isActive() ? 'Yes' : 'No',
      internalReport: this.isInternalReport() ? 'Yes' : 'No',
      reportType: this.reportType(),
      pageView: this.pageView(),
      filterPaneEnabled: this.filterPaneEnabled(),
      navContentPaneEnabled: this.navContentPaneEnabled(),
      assignedInstitutions: this.assignedInstitutions(),
    };

    if (this.isAddReport()) {
      this.startLoading();
      this.reportService.createReport(newReport).subscribe({
        next: (response) => {
          const reportWithId = { ...newReport, id: response.id, reportId: response.reportId };
          this.reports.set([...this.reports(), reportWithId]);
          this.onSearch(reportWithId.id, () => {
            this.showNotification('Report created successfully.', 'success');
          });
          this.isAddReport.set(false);
          this.isEditReport.set(false);
          this.availableInstitutionSearch.set('');
          this.assignedInstitutions.set([...(reportWithId.assignedInstitutions ?? [])]);
          this.transferList?.scrollToTop();
          this.stopLoading();
        },
        error: (err) => {
          console.error('Error creating report:', err);
          this.showNotification('Failed to create Report. Please try again.', 'error');
          this.stopLoading();
        },
      });
    } else {
      const selected = this.selectedReport();
      if (selected) {
        const preserveSelectedReportId = selected.id;
        this.startLoading();
        this.reportService.updateReport(newReport).subscribe({
          next: () => {
            this.onSearch(preserveSelectedReportId, () => {
              this.showNotification('Report updated successfully.', 'success');
            });
            this.isAddReport.set(false);
            this.isEditReport.set(false);
            this.availableInstitutionSearch.set('');
            this.assignedInstitutions.set([...(newReport.assignedInstitutions ?? [])]);
            this.transferList?.scrollToTop();
            this.stopLoading();
          },
          error: (err) => {
            console.error('Error updating report:', err);
            this.showNotification('Failed to update Report. Please try again.', 'error');
            this.stopLoading();
          }
        });
      }
    }
  }

  confirmDelete(): void {
    const report = this.reportToDelete();
    if (report && report.id) {
      this.startLoading();
      this.reportService.deleteReport(report.id).subscribe({
        next: () => {
          this.reports.set(this.reports().filter(r => r.id !== report.id));
          this.onSearch();
          if (this.selectedReport()?.id === report.id) {
            this.selectedReport.set(this.reports().length > 0 ? this.reports()[0] : null);
            if (this.selectedReport()) {
              this.onSelectReport(this.selectedReport()!);
            }
          }
          this.showDeleteModal.set(false);
          this.reportToDelete.set(null);
          this.showNotification('Report deleted successfully.', 'success');
          this.stopLoading();
        },
        error: (err) => {
          console.error('Error deleting report:', err);
          this.showNotification('Failed to delete Report. Please try again.', 'error');
          this.showDeleteModal.set(false);
          this.stopLoading();
        }
      });
    } else if (report) {
      this.showNotification('Cannot delete Report without ID.', 'error');
      this.showDeleteModal.set(false);
    }
  }

  onCancel(): void {
    this.availableInstitutionSearch.set('');
    this.selectedAvailable.set([]);
    this.selectedAssigned.set([]);
    this.transferList?.scrollToTop();
    if (this.isAddReport()) {
      const firstReport = this.reports().length > 0 ? this.reports()[0] : null;
      this.isAddReport.set(false);
      this.isEditReport.set(false);
      this.selectedReport.set(firstReport);
      if (firstReport) {
        this.assignedInstitutions.set([...(firstReport.assignedInstitutions ?? [])]);
        this.onSelectReport(firstReport);
      } else {
        this.assignedInstitutions.set([]);
      }
    } else {
      const report = this.selectedReport();
      this.isEditReport.set(false);
      if (report) {
        this.assignedInstitutions.set([...(report.assignedInstitutions ?? [])]);
        this.onSelectReport(report);
      }
    }
  }

  onSearchKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.onSearch();
    }
  }

  onFormKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && this.isEditReport() && this.isFormValid()) {
      this.onSave();
    }
  }
}